/**
 * Single place that turns AutoDispatch's server-authoritative payment block into a
 * label the driver can read.
 *
 * The server already computes the whole state machine in
 * autodisp-master/routes/paymentAPI.js summarizeTrip() and ships it on every
 * trip-details payload (socket job `payment` object, and the same fields stamped
 * onto each trip by routes/driver.js for the REST route/itinerary path). We read
 * those fields verbatim — we do NOT re-derive payment state from fares or
 * balances client-side, because the server is the authority on the trip balance
 * and any local arithmetic would be a second, drifting source of truth.
 *
 * Field semantics, per summarizeTrip():
 *   paid            = paymentStatus === 'Settled' OR accountBilled
 *   accountBilled   = billed to an account; never collected from the rider
 *   paymentPending  = a payment is in flight (hosted page / webhook not back yet)
 *   priceFinal      = the fare is finalized (true for fixed-price even at $0)
 *   paymentRequired = !accountBilled && fare > 0
 *   payable         = priceFinal && paymentRequired && !paid && !paymentPending
 */

export const TRIP_PAYMENT_STATE = {
  UNKNOWN: 'unknown',
  NEEDS_REVIEW: 'needsReview',
  ACCOUNT_BILLED: 'accountBilled',
  PAID: 'paid',
  PENDING: 'pending',
  FARE_NOT_FINAL: 'fareNotFinal',
  NO_CHARGE: 'noCharge',
  UNPAID: 'unpaid',
};

const LABELS = {
  [TRIP_PAYMENT_STATE.UNKNOWN]: '',
  [TRIP_PAYMENT_STATE.NEEDS_REVIEW]: 'Needs review',
  [TRIP_PAYMENT_STATE.ACCOUNT_BILLED]: 'Account billed',
  [TRIP_PAYMENT_STATE.PAID]: 'Paid',
  [TRIP_PAYMENT_STATE.PENDING]: 'Payment pending',
  [TRIP_PAYMENT_STATE.FARE_NOT_FINAL]: 'Fare not final',
  [TRIP_PAYMENT_STATE.NO_CHARGE]: 'No charge',
  [TRIP_PAYMENT_STATE.UNPAID]: 'Unpaid',
};

function isTrue(v) {
  return v === true || v === 1 || String(v).toLowerCase() === 'true';
}

/**
 * Canonical key for a trip GUID (AutoDispatch `Trip.Id` / Live `info.jobID` /
 * Itinerary `reservation.id`). Every payment map, settlement match and QR must be
 * keyed by THIS id — never by the short TripNo/jobNumber, which the server's
 * trip:paymentSettled push does not carry.
 */
export function tripIdKey(id) {
  if (id === null || id === undefined) return '';
  return String(id).trim().toLowerCase();
}

/**
 * Merge a live settlement (trip:paymentSettled / tripCollectionResult / an acknowledged
 * cash payment) into a server payment block so every reader agrees. A settlement always
 * wins over a block captured before it; needsReconciliation is left to the resolver.
 */
export function mergePaymentOverride(payment, override) {
  const base = payment && typeof payment === 'object' ? payment : null;
  if (!override || typeof override !== 'object') return base;
  const merged = { ...(base || {}) };
  if (override.paid === true) {
    merged.paid = true;
    merged.paymentStatus = 'Settled';
    merged.paymentPending = false;
    merged.priceFinal = true;
    merged.payable = false;
  } else if (override.paymentPending === true) {
    merged.paymentPending = true;
  }
  if (override.priceFinal === true) merged.priceFinal = true;
  if (Number.isFinite(Number(override.paymentAmount))) merged.paymentAmount = Number(override.paymentAmount);
  if (override.paymentMethod) merged.paymentMethod = override.paymentMethod;
  return merged;
}

/**
 * @param {object|null} payment  The server's payment block for one trip.
 * @param {object}  [opts]
 * @param {boolean} [opts.settledLive] True when trip:paymentSettled has arrived for
 *   this trip in the current session. A live settlement always wins over a payload
 *   captured before it.
 * @param {boolean} [opts.needsReconciliation] From the trip:paymentSettled payload.
 * @returns {{state: string, label: string, isPaid: boolean, isFinal: boolean}}
 */
export function resolveTripPaymentStatus(payment, opts = {}) {
  const { settledLive = false, needsReconciliation = false } = opts;

  // Money arrived but the server couldn't reconcile it — surface that above
  // everything else; it needs a human, and calling it "Paid" would hide it.
  if (needsReconciliation) {
    return build(TRIP_PAYMENT_STATE.NEEDS_REVIEW, false, true);
  }

  if (!payment || typeof payment !== 'object') {
    // No payload yet. A live settlement is still trustworthy on its own.
    return settledLive
      ? build(TRIP_PAYMENT_STATE.PAID, true, true)
      : build(TRIP_PAYMENT_STATE.UNKNOWN, false, false);
  }

  const accountBilled = isTrue(payment.accountBilled);
  const paid = settledLive || isTrue(payment.paid) ||
    String(payment.paymentStatus || '').trim().toLowerCase() === 'settled';
  const pending = isTrue(payment.paymentPending);
  const priceFinal = isTrue(payment.priceFinal);
  const paymentRequired = isTrue(payment.paymentRequired);

  // Account-billed is a distinct outcome from a rider payment, and summarizeTrip
  // already reports paid:true for it — check it first so it isn't mislabelled "Paid".
  if (accountBilled) return build(TRIP_PAYMENT_STATE.ACCOUNT_BILLED, true, true);
  if (paid) return build(TRIP_PAYMENT_STATE.PAID, true, true);
  if (pending) return build(TRIP_PAYMENT_STATE.PENDING, false, priceFinal);
  if (!priceFinal) return build(TRIP_PAYMENT_STATE.FARE_NOT_FINAL, false, false);
  // priceFinal with nothing owed — covers a genuinely final $0 fare, which HBSS
  // cannot charge for (see the summarizeTrip note about the $0 fixed-price case).
  if (!paymentRequired) return build(TRIP_PAYMENT_STATE.NO_CHARGE, false, true);
  return build(TRIP_PAYMENT_STATE.UNPAID, false, true);
}

function build(state, isPaid, isFinal) {
  return { state, label: LABELS[state], isPaid, isFinal };
}

/** Tailwind classes for the status chip, keyed by state. */
export const TRIP_PAYMENT_CHIP_CLASS = {
  [TRIP_PAYMENT_STATE.NEEDS_REVIEW]: 'bg-red-100 text-red-800 border-red-200',
  [TRIP_PAYMENT_STATE.ACCOUNT_BILLED]: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  [TRIP_PAYMENT_STATE.PAID]: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  [TRIP_PAYMENT_STATE.PENDING]: 'bg-amber-100 text-amber-800 border-amber-200',
  [TRIP_PAYMENT_STATE.FARE_NOT_FINAL]: 'bg-slate-100 text-slate-700 border-slate-200',
  [TRIP_PAYMENT_STATE.NO_CHARGE]: 'bg-slate-100 text-slate-700 border-slate-200',
  [TRIP_PAYMENT_STATE.UNPAID]: 'bg-orange-100 text-orange-800 border-orange-200',
  [TRIP_PAYMENT_STATE.UNKNOWN]: 'bg-slate-100 text-slate-500 border-slate-200',
};

/**
 * Binary Paid/Unpaid view of the same resolver, for cards that must ALWAYS show one of
 * exactly two values (the itinerary waypoint card). Never returns null — a trip with no
 * payment data still gets a badge.
 *
 * "Paid" requires positive evidence that nothing is owed by the passenger:
 *   paid / accountBilled / noCharge (a final $0 fare)
 * Everything else reads "Unpaid", including:
 *   pending       — money is in flight but not confirmed settled
 *   fareNotFinal  — a metered trip mid-run; not settled
 *   needsReview   — money arrived but the server couldn't reconcile it
 *   unknown       — no payment block yet
 * Unknown deliberately fails to "Unpaid": if we're wrong that way the driver checks and
 * finds it already paid, whereas a wrong "Paid" silently skips collecting a real fare.
 *
 * @returns {{isPaid: boolean, label: 'Paid'|'Unpaid', state: string}}
 */
/** States that mean nothing is owed by the passenger. */
const PAID_STATES = [
  TRIP_PAYMENT_STATE.PAID,
  TRIP_PAYMENT_STATE.ACCOUNT_BILLED,
  TRIP_PAYMENT_STATE.NO_CHARGE,
];

/** Binary Paid/Unpaid from an already-resolved state (see resolveTripPaidBinary). */
export function isPaidState(state) {
  return PAID_STATES.indexOf(state) !== -1;
}

export function resolveTripPaidBinary(payment, opts = {}) {
  const status = resolveTripPaymentStatus(payment, opts);
  const isPaid = isPaidState(status.state);
  return { isPaid, label: isPaid ? 'Paid' : 'Unpaid', state: status.state };
}

export default resolveTripPaymentStatus;
